import { apiGet, apiPost, apiPatch, apiDelete } from '../api';

/**
 * Report-system enums — kept in sync with the backend Zod schemas in
 * `backend/src/schemas/index.ts` and the live D1 schema repaired in
 * migration 0055 (#1742). When updating these unions, update the
 * Zod source and the user-facing UI strings in:
 *   - frontend/src/components/MessageActionSheet.tsx
 *   - frontend/src/pages/PublicProfilePage.tsx
 *   - frontend/src/components/AdminReportDetailModal.tsx (display maps)
 *   - frontend/src/pages/AdminReportsPage.tsx (display maps)
 */
export type ReportContextType = 'message' | 'event' | 'club' | 'profile' | 'user';
export type ReportReasonValue =
  | 'spam'
  | 'harassment'
  | 'hate_speech'
  | 'safety_concern'
  | 'inappropriate_content'
  | 'other';
export type ReportStatus = 'open' | 'under_review' | 'resolved' | 'dismissed';

export interface UserReport {
  id: string;
  reporter_id: string;
  reported_id: string | null;
  context_type: ReportContextType;
  context_id: string | null;
  reason: ReportReasonValue;
  notes: string | null;
  status: ReportStatus;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: number | null;
  created_at: number;
  updated_at: number;
  // Joined fields
  reporter_first_name?: string;
  reporter_last_name?: string;
  reported_first_name?: string;
  reported_last_name?: string;
  reviewer_first_name?: string;
  reviewer_last_name?: string;
}

/** User info returned in report details */
export interface ReportUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string;
}

/**
 * Resolved context for moderation reports (#536 message variant, #1479 event/club/user/profile variants).
 * Tagged union keyed on `kind` matching the report's context_type. Backend
 * omits `context` when the underlying entity row no longer exists, so the
 * modal must handle the undefined case.
 */
export interface ReportMessageContext {
  kind: 'message';
  message_text: string | null;
  message_author_id: string | null;
  message_author_name: string | null;
  club_id: string | null;
  club_name: string | null;
  message_timestamp: number | null;
  is_deleted: boolean;
}

export interface ReportEventContext {
  kind: 'event';
  event_id: string;
  title: string;
  description: string | null;
  starts_at: number;
  location_name: string | null;
  club_id: string;
  club_name: string;
  owner_id: string;
  owner_name: string;
  is_cancelled: boolean;
}

export interface ReportClubContext {
  kind: 'club';
  club_id: string;
  slug: string;
  name: string;
  description: string | null;
  owner_id: string;
  owner_name: string;
  member_count: number;
  created_at: number;
}

export interface ReportUserContext {
  kind: 'user' | 'profile';
  user_id: string;
  display_name: string;
  tagline: string | null;
  location: string | null;
  primary_sport: string | null;
  club_count: number;
  created_at: number;
  is_deleted: boolean;
}

export type ReportContext =
  | ReportMessageContext
  | ReportEventContext
  | ReportClubContext
  | ReportUserContext;

/** Full report detail response from GET /reports/admin/reports/:id */
export interface ReportDetail extends UserReport {
  reporter?: ReportUser;
  reported_user?: ReportUser;
  reviewer?: ReportUser;
  context?: ReportContext;
}

/** Alias for AdminReportDetailModal compatibility */
export type Report = UserReport;

export interface ListReportsParams {
  status?: ReportStatus;
  reason?: ReportReasonValue;
  context_type?: ReportContextType;
  limit?: number;
}

export interface UpdateReportParams {
  status: ReportStatus;
  admin_notes?: string;
}

/**
 * Payload for creating a new report (user-facing)
 */
export interface CreateReportPayload {
  reported_id?: string;          // User being reported (optional for non-user-targeted reports)
  context_type: ReportContextType; // What's being reported
  context_id?: string;            // ID of the message/club/event (if applicable)
  reason: ReportReasonValue;      // Report reason
  notes?: string;                 // Additional details
}

export interface CreateReportResponse {
  success: boolean;
  message: string;
  report_id: string;
}

/**
 * Response from POST /reports/admin/users/:id/suspend (#3260).
 * Backend envelope is `{ ok: true, message }` (mirrors the revert-email route),
 * NOT the `{ success }` shape used by the report-status routes.
 */
export interface SuspendUserResponse {
  ok: boolean;
  message: string;
}

/**
 * Platform-wide statistics for admin dashboard (#1062)
 */
export interface PlatformStats {
  // Primary stats (top row cards)
  totalMembers: number;
  totalClubs: number;
  activeClubs: number;
  eventsThisMonth: number;
  // Secondary stats (platform overview section)
  newMembers7d: number;
  upcomingEvents: number;
  checkIns30d: number;
}

export const reportsApi = {
  /**
   * Submit a report (user-facing)
   * POST /reports
   */
  createReport: async (payload: CreateReportPayload): Promise<CreateReportResponse> => {
    return apiPost<CreateReportResponse>('/reports', payload);
  },

  /**
   * List all reports (admin only)
   */
  listReports: async (params: ListReportsParams): Promise<{ reports: UserReport[] }> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.reason) searchParams.append('reason', params.reason);
    if (params.context_type) searchParams.append('context_type', params.context_type);
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const url = `/reports/admin/reports${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return apiGet<{ reports: UserReport[] }>(url);
  },

  /**
   * Get single report with full context (admin only)
   */
  getReport: async (id: string): Promise<{ report: ReportDetail }> => {
    return apiGet<{ report: ReportDetail }>(`/reports/admin/reports/${id}`);
  },

  /**
   * Update report status (admin only)
   */
  updateReport: async (id: string, params: UpdateReportParams): Promise<{ success: boolean; message: string }> => {
    return apiPatch<{ success: boolean; message: string }>(`/reports/admin/reports/${id}`, params);
  },

  /**
   * Delete a message (admin only, for moderation)
   */
  deleteMessage: async (messageId: string): Promise<{ success: boolean; message: string }> => {
    return apiDelete<{ success: boolean; message: string }>(`/reports/admin/messages/${messageId}`);
  },

  /**
   * Suspend a user account (admin only, for moderation).
   * #3260: the "eject" half of the Apple 1.2 24h moderation SLA. Revokes all
   * of the user's sessions server-side and blocks new logins until unsuspended.
   * POST /reports/admin/users/:id/suspend
   */
  suspendUser: async (userId: string, reason: string): Promise<SuspendUserResponse> => {
    return apiPost<SuspendUserResponse>(`/reports/admin/users/${userId}/suspend`, { reason });
  },

  /**
   * Get user's own reports
   */
  myReports: async (): Promise<{ reports: UserReport[] }> => {
    return apiGet<{ reports: UserReport[] }>('/reports/my-reports');
  },

  /**
   * Get platform-wide statistics (admin only)
   * GET /reports/admin/stats (#1089 - correct route path)
   */
  getStats: async (): Promise<{ stats: PlatformStats }> => {
    return apiGet<{ stats: PlatformStats }>('/reports/admin/stats');
  },

  /**
   * Get subscription change audit logs (admin only)
   * GET /reports/admin/audit-logs/subscriptions (#1089 - correct route path)
   */
  getSubscriptionAuditLogs: async (limit = 50): Promise<{ logs: SubscriptionAuditLog[] }> => {
    return apiGet<{ logs: SubscriptionAuditLog[] }>(`/reports/admin/audit-logs/subscriptions?limit=${limit}`);
  },

  /**
   * Get platform members with club affiliations (admin only)
   * GET /reports/admin/members (#1299)
   */
  getMembers: async (params: ListMembersParams = {}): Promise<PlatformMembersResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.sort) searchParams.append('sort', params.sort);
    if (params.order) searchParams.append('order', params.order);

    const url = `/reports/admin/members${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return apiGet<PlatformMembersResponse>(url);
  },
};

/**
 * Platform member with club affiliations (#1299)
 */
export interface PlatformMember {
  id: string;
  email: string;
  display_name: string | null;
  created_at: number;
  last_active_at: number | null;
  clubs: {
    id: string;
    name: string;
    role: 'member' | 'leader' | 'owner';
  }[];
}

export interface PlatformMembersResponse {
  members: PlatformMember[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface ListMembersParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: 'created_at' | 'email' | 'first_name';
  order?: 'asc' | 'desc';
}

/**
 * Subscription audit log entry (#842)
 */
export interface SubscriptionAuditLog {
  id: string;
  admin_id: string;
  action: string;
  target_type: string;
  target_id: string;
  details: {
    club_name: string;
    old_tier: string;
    new_tier: string;
    expires_at: number | null;
  } | null;
  created_at: number;
  admin_first_name: string | null;
  admin_last_name: string | null;
}
